// Annual-leave domain helpers.
//
// Leave lives on the dataset as leave.{personId}.{weekKey} = 0.5 | 1,
// where weekKey is the ISO date of the week's Monday. The grid covers
// 52 weeks starting from the Monday on/before the 1st of the first
// forecast month. Leave rolls up into per-month fractions (keyed by
// YYYY-MM) that reduce a person's available capacity on the Capacity
// matrix.

const iso = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

const parseMonthKey = (key) => {
  const m = /^(\d{4})-(\d{2})$/.exec(String(key || ""));
  return m ? { year: +m[1], monthIndex: +m[2] - 1 } : null;
};

// 52 weeks: [{ key, label, month, monthStart }] — `month` is the "YYYY-MM"
// the week belongs to (by its Monday), monthStart flags the first week of
// each month for header grouping.
export function forecastWeeks(rawData) {
  const first = rawData?.meta?.months?.[0];
  const parsed = parseMonthKey(first);
  if (!parsed) return [];
  const { year, monthIndex } = parsed;
  const start = new Date(year, monthIndex, 1);
  // Roll back to Monday (getDay(): Mon=1 … Sun=0).
  const dow = start.getDay();
  start.setDate(start.getDate() - ((dow + 6) % 7));

  const out = [];
  let prevMonth = null;
  for (let i = 0; i < 52; i += 1) {
    const d = new Date(start);
    d.setDate(start.getDate() + i * 7);
    const monthKey = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
    out.push({
      key: iso(d),
      label: `${d.getDate()}/${String(d.getMonth() + 1).padStart(2, "0")}`,
      day: d.getDate(),
      month: monthKey,
      monthStart: monthKey !== prevMonth,
    });
    prevMonth = monthKey;
  }
  return out;
}

// { personId: { "YYYY-MM": fractionOfMonthOnLeave } } — a person with 2 of
// a month's 4 weeks marked as full leave is 0.5 away that month.
export function leaveByPersonMonth(rawData) {
  const weeks = forecastWeeks(rawData);
  const weeksPerMonth = {};
  for (const w of weeks) weeksPerMonth[w.month] = (weeksPerMonth[w.month] || 0) + 1;
  const weekMonth = Object.fromEntries(weeks.map((w) => [w.key, w.month]));

  const out = {};
  const leave = rawData?.leave || {};
  for (const [personId, byWeek] of Object.entries(leave)) {
    const acc = {};
    for (const [wk, val] of Object.entries(byWeek || {})) {
      const month = weekMonth[wk];
      if (!month || !val) continue;
      acc[month] = (acc[month] || 0) + val;
    }
    out[personId] = Object.fromEntries(
      Object.entries(acc).map(([mo, wks]) => [mo, Math.min(1, wks / (weeksPerMonth[mo] || 4))])
    );
  }
  return out;
}

// Capacity band that accounts for leave: leave is ADDITIVE — it stacks on
// top of the project load rather than shrinking the denominator. A person
// booked to 80% who has a full week of leave (25% of the month) reads as
// 105% total, not 80% doubled. Available capacity is always the full FTE.
export function bandWithLeave(load, leaveFrac = 0) {
  const total = (load || 0) + (leaveFrac || 0);
  if (total <= 0.0001) return "empty";
  if (total > 1 + 0.0001) return "over";
  if (total >= 0.8) return "balanced";
  return "available";
}

// Total weeks of leave for a person (0.5s count as half).
export function totalLeaveWeeks(byWeek) {
  return Object.values(byWeek || {}).reduce((s, v) => s + (v || 0), 0);
}
